/**
 * @file resolveDesktopPinWrite.ts
 * @description Builds the single `update` payload behind `keep` and
 * `stopKeeping`. Consumers: those two functions and nothing else. Pure — it
 * takes the map as read at call time and returns Firestore field paths, so the
 * write semantics can be pinned by tests without a store.
 *
 * Three rules live here. A `keep` on a path already pinned **reuses** the
 * oldest matching entry and preserves its `meta.created`, so pinning twice
 * never mints a second pin. Any further matching entry is deleted in the same
 * payload, which is how a map that already drifted repairs itself. And a `files`
 * scope with an empty `fileUids` is an unpin spelled badly, so it is normalized
 * to one.
 *
 * A reused entry keeps every `meta` key already on it, including any the agent
 * wrote, but `meta.userId`, `meta.lastModified` and `meta.agentType` are
 * restamped for this write — `AgentType.User` on an entry the agent authored is
 * deliberate and says "the last writer was the person", not "the agent made
 * this". Whoever last changed a pin's scope is who the field names.
 *
 * The pin map is touched by field path (`desktopPins.{pinId}`), never rewritten
 * whole, so two tabs pinning different artifacts cannot lose each other's entry.
 * `desktopPinPaths` is the exception and it is deliberate: it is recomputed
 * whole from the post-write map rather than appended to, which is what lets it
 * heal. The cost is a last-writer-wins array — two simultaneous pins can leave
 * the projection missing the loser's path until that user's next pin write.
 * `desktopPins` is the source of truth throughout, so the only symptom is a
 * claim banner that under-reports a holder for a while.
 */

import { deleteField, serverTimestamp, type UpdateData } from 'firebase/firestore';
import { v4 as uuidv4 } from 'uuid';

import { AgentType, type DesktopPin, type DesktopPinScope, type User } from '#types';
import { resolveDesktopPinEntries } from './resolveDesktopPinEntries';

export type DesktopPinWriteRequest = {
    /** Signed-in uid. Names the document written, and is stamped on `meta.userId`. */
    userId: string;
    /** `User.desktopPins` as read at call time. */
    pins: Readonly<Record<string, DesktopPin | undefined>> | undefined;
    /** `User.desktopPinPaths` as read at call time, used only to skip a write that would change nothing. */
    pinPaths: readonly string[] | undefined;
    /** A path from `resolveDesktopPinPath`, never a raw artifact path. */
    pinPath: string;
    /** `null` unpins. A `files` scope with no uids is treated as `null`. */
    scope: DesktopPinScope | null;
};

const sameProjection = (
    current: readonly string[] | undefined,
    next: readonly string[],
): boolean =>
    current !== undefined
    && current.length === next.length
    && current.every((path, index) => path === next[index]);

export const resolveDesktopPinWrite = ({
    userId,
    pins,
    pinPaths,
    pinPath,
    scope,
}: DesktopPinWriteRequest): UpdateData<User> | null => {
    const intent = scope !== null && !(scope.kind === 'files' && scope.fileUids.length === 0)
        ? scope
        : null;

    const entries = resolveDesktopPinEntries(pins, pinPath);
    const reused = intent === null ? undefined : entries[0];
    const removed = intent === null ? entries : entries.slice(1);
    const writtenPinId = intent === null ? null : reused?.pinId ?? uuidv4();

    const nextPaths = new Set<string>();
    for (const [pinId, pin] of Object.entries(pins ?? {})) {
        const isRemoved = removed.some((entry) => entry.pinId === pinId);
        if (!isRemoved && pin?.artifactPath) {
            nextPaths.add(pin.artifactPath);
        }
    }
    if (writtenPinId !== null) {
        nextPaths.add(pinPath);
    }
    const desktopPinPaths = [...nextPaths].sort();

    if (writtenPinId === null && removed.length === 0 && sameProjection(pinPaths, desktopPinPaths)) {
        return null;
    }

    const update: UpdateData<User> = { desktopPinPaths };
    for (const entry of removed) {
        update[`desktopPins.${entry.pinId}`] = deleteField();
    }
    if (writtenPinId !== null && intent !== null) {
        const pin: DesktopPin = {
            artifactPath: pinPath,
            meta: {
                ...(reused?.pin.meta ?? {}),
                created: reused?.pin.meta?.created ?? serverTimestamp(),
                lastModified: serverTimestamp(),
                userId,
                agentType: AgentType.User,
                scope: intent,
            },
        };
        update[`desktopPins.${writtenPinId}`] = pin;
    }

    return update;
};
